import { useState, useCallback, useRef, useEffect } from 'react';
import { zafClient, checkCpfExists, checkCnpjExists, searchUsers } from '@services/Zendesk';
import type { User } from 'types/UserZendesk';
import { useValidation } from './validation';
import { formateCpf } from '@hooks/masks';

export function useUserValidation() {
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [cnpjError, setCnpjError] = useState<string | null>(null);

  const [existUser, setExistUser] = useState<User | null>(null);
  const [existUserCpfFormatted, setExistUserCpfFormatted] = useState<User | null>(null);
  const [existUserEmail, setExistUserEmail] = useState<User | null>(null);

  const [isCpfComplete, setIsCpfComplete] = useState(false);
  const [isCnpjComplete, setIsCnpjComplete] = useState(false);

  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cnpjTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    isValidEmail,
    isValidCPFStructure,
    isValidCNPJStructure,
  } = useValidation();

  const handleCpfChange = useCallback(
    async (cpf: string) => {
      const rawValue = cpf.replace(/\D/g, '').slice(0, 11);
      const formattedValue = formateCpf(rawValue);

      setCpfError(null);
      setExistUser(null);
      setExistUserCpfFormatted(null);
      setIsCpfComplete(rawValue.length === 11);

      if (rawValue.length !== 11) {
        return formattedValue;
      }

      if (/^(\d)\1{10}$/.test(rawValue)) {
        setCpfError('CPF com 11 dígitos repetidos inválido');
        return formattedValue;
      }

      if (!isValidCPFStructure(rawValue)) {
        setCpfError('CPF inválido');
        return formattedValue;
      }

      const searchFormatted = checkCpfExists(zafClient, formattedValue);
      const searchRaw = checkCpfExists(zafClient, rawValue);


      try {
        const [formattedResult, rawResult] = await Promise.all([searchFormatted, searchRaw]);


        let userFormatted: User | null = null;
        let userRaw: User | null = null;

        if (formattedResult.exists) {
          userFormatted = formattedResult.user as unknown as User;
        }

        if (rawResult.exists) {
          userRaw = rawResult.user as unknown as User;
        }

        if (userFormatted && userRaw && userFormatted.id === userRaw.id) {
          userRaw = null; 
        }

        setExistUserCpfFormatted(userFormatted);
        setExistUser(userRaw);

        if (userFormatted && userRaw) {
          setCpfError('CPF já cadastrado em dois usuários (formatado e sem formatação)');
        } else if (userFormatted) {
          setCpfError('Já existe um usuário com este CPF formatado');
        } else if (userRaw) {
          setCpfError('Já existe um usuário com este CPF (apenas números)');
        }

      } catch (error) {
        console.error('Erro geral ao validar CPF:', error);
      }

      return formattedValue;
    },
    []
  );



  const handleCnpjChange = useCallback(
    async (cnpj: string) => {
      const rawValue = cnpj.replace(/\D/g, '');
      setCnpjError(null);
      setExistUser(null);
      setIsCnpjComplete(rawValue.length === 14 || rawValue.length === 18);

      if (cnpjTimeoutRef.current) {
        clearTimeout(cnpjTimeoutRef.current);
      }

      if (rawValue.length === 14 || rawValue.length === 18) {
        if (!isValidCNPJStructure(rawValue)) {
          setCnpjError('CNPJ inválido');
          return;
        }
      }

      cnpjTimeoutRef.current = setTimeout(async () => {
        if (rawValue.length === 14 || rawValue.length === 18) {
          try {
            const { exists, user } = await checkCnpjExists(zafClient, rawValue);
            if (exists) {
              setCnpjError(`Já existe um usuário com este CNPJ`);
              setExistUser(user as unknown as User);
            }
          } catch (error) {
            console.error('Error checking CNPJ:', error);
          }
        }
      }, 500);
    },
    [isValidCNPJStructure]
  );

  const handleEmailChange = useCallback(
    async (email: string) => {
      setEmailError(null);
      setExistUserEmail(null);

      if (email && !isValidEmail(email)) {
        setEmailError('Email inválido');
        return email;
      }

      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }

      if (email && email.includes('@') && email.length >= 5) {
        emailTimeoutRef.current = setTimeout(async () => {
          try {
            const existEmail = await searchUsers(zafClient, `email:${email}`);
            if (existEmail.length > 0) {
              setEmailError(`Já existe um usuário com este email ${existEmail[0].user_fields.perfil_da_conta !== null ? `(Tipo de conta: ${existEmail[0].user_fields.perfil_da_conta})` : ''}`);
              setExistUserEmail(existEmail[0]);
            }
          } catch (error) {
            console.error('Error checking email:', error);
          }
        }, 500);
      }

      return email;
    },
    []
  );

  useEffect(() => {
    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, []);

  return {
    cpfError,
    emailError,
    cnpjError,
    existUser,
    existUserEmail,
    handleCpfChange,
    handleEmailChange,
    handleCnpjChange,
    isCpfComplete,
    isCnpjComplete,
    existUserCpfFormatted
  };
}